import type { QueryClient } from '@tanstack/react-query';

import type { AIEvent, SessionResponse } from '@doger/core/shared/api';

import { CHAT_QUERY_KEYS } from './chat.query-keys';

export class ChatCacheManager {
  public constructor(private readonly queryClient: QueryClient) {}

  public addNewChat(args: { session: SessionResponse }) {
    this.queryClient.setQueryData(
      CHAT_QUERY_KEYS.queries.sessions(),
      (old: SessionResponse[]) => {
        if (!old) {
          return [args.session];
        }

        return [args.session, ...old];
      }
    );

    this.queryClient.setQueryData(
      CHAT_QUERY_KEYS.queries.session(args.session.id),
      () => ({
        id: args.session.id,
        appName: args.session.appName,
        userId: args.session.userId,
        state: args.session.state,
        lastUpdateTime: args.session.lastUpdateTime,
        meta: args.session.meta,
        events: [],
      })
    );
  }

  public addUserMessageToSession(args: { sessionId: string; message: string }) {
    this.queryClient.setQueryData(
      CHAT_QUERY_KEYS.queries.session(args.sessionId),
      (old: SessionResponse) => {
        if (!old) return old;

        return {
          ...old,
          events: [
            ...old.events,
            {
              id: `optimistic-${Date.now()}`,
              content: {
                role: 'user',
                parts: [{ text: args.message }],
              },
            },
          ],
        };
      }
    );
  }

  public addAgentResponseToChat(args: { sessionId: string; event: AIEvent }) {
    this.queryClient.setQueryData(
      CHAT_QUERY_KEYS.queries.session(args.sessionId),
      (old: SessionResponse) => {
        if (!old) return old;

        return { ...old, events: [...old.events, args.event] };
      }
    );
  }

  public removeLastAgentReponseFromChat(args: { sessionId: string }) {
    this.queryClient.setQueryData(
      CHAT_QUERY_KEYS.queries.session(args.sessionId),
      (old: SessionResponse) => {
        if (!old) return old;

        return { ...old, events: old.events.slice(0, -1) };
      }
    );
  }

  public ammendLastAgentResponseToChat(args: {
    sessionId: string;
    text: string;
  }) {
    this.queryClient.setQueryData(
      CHAT_QUERY_KEYS.queries.session(args.sessionId),
      (old: SessionResponse) => {
        if (!old) return old;

        const lastEvent = old.events.at(-1);

        if (!lastEvent?.content?.parts) return old;

        if (lastEvent.content.parts.length === 1) {
          const lastEventContentPart = lastEvent.content.parts[0];

          return {
            ...old,
            events: [
              ...old.events.slice(0, -1),
              {
                ...lastEvent,
                content: {
                  ...lastEvent.content,
                  parts: [
                    {
                      ...lastEventContentPart,
                      text: lastEventContentPart.text + args.text,
                    },
                  ],
                },
              },
            ],
          };
        }

        return old;
      }
    );
  }
}
